import React, { useState } from "react";
import PageHeader from "../components/PageHeader";
import { useTeam } from "../../hooks/useFirebase";
import { TasksService } from "../../firebase/databaseService";

const BulkUploadPage = () => {
  const { teamMembers } = useTeam();

  const [pasteData, setPasteData] = useState("");
  const [parsedRows, setParsedRows] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  const [assignForm, setAssignForm] = useState({
    title: "",
    taskType: "calling",
    assignedTo: "",
    assignedName: "",
    priority: "medium",
    dueDate: "",
    description: "",
  });

  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState("");

  // ==========================================
  // EXCEL PASTE SE DATA PARSE KARO
  // Excel se copy kiya data TAB-separated hota hai
  // ==========================================
  const handleParse = () => {
    setUploadError("");
    setUploadSuccess("");

    if (!pasteData.trim()) {
      setUploadError("Pehle Excel se data copy karke paste karo!");
      return;
    }

    const lines = pasteData.trim().split("\n");

    if (lines.length < 2) {
      setUploadError("Kam se kam 2 lines honi chahiye (header + data)!");
      return;
    }

    const rows = [];

    // Skip header line (line 0)
    // Data lines se parse karo
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      // TAB separated ya COMMA separated detect karo
      let cells;
      if (line.includes("\t")) {
        // Tab-separated (Excel se copy)
        cells = line.split("\t");
      } else if (line.includes(",")) {
        // Comma-separated (CSV)
        cells = line.split(",");
      } else {
        // Space ya pipe separated
        cells = line.split(/\s{2,}|\s*\|\s*/);
      }

      const leadId = (cells[0] || "").trim();
      const businessName = (cells[1] || "").trim();
      const category = (cells[2] || "").trim();
      const contactNumber = (cells[3] || "").trim();

      // Skip empty rows
      if (!leadId && !businessName && !contactNumber) continue;

      // Valid row check
      if (!businessName && !contactNumber) continue;

      rows.push({
        id: `row_${Date.now()}_${i}`,
        leadId: leadId || `AUTO-${String(i).padStart(3, "0")}`,
        businessName,
        category,
        contactNumber,
        // Employee fill karega
        callDate: "",
        decisionMaker: "",
        requirement: "",
        painPoint: "",
        status: "pending",
        employeeNotes: "",
        updatedBy: "",
        updatedAt: null,
      });
    }

    if (rows.length === 0) {
      setUploadError("Data parse nahi hua! Format check karo. Excel se copy karke paste karo.");
      return;
    }

    setParsedRows(rows);
    setShowPreview(true);
    setUploadSuccess(`✅ ${rows.length} rows successfully parse hue!`);
  };

  // ==========================================
  // ASSIGN FORM CHANGE
  // ==========================================
  const handleAssignChange = (e) => {
    const { name, value } = e.target;
    setAssignForm((prev) => ({ ...prev, [name]: value }));

    if (name === "assignedTo") {
      const member = teamMembers.find((m) => m.id === value);
      if (member) {
        setAssignForm((prev) => ({
          ...prev,
          assignedTo: value,
          assignedName: member.name,
          assignedRole: member.role,
        }));
      }
    }
  };

  // ==========================================
  // UPLOAD & ASSIGN TASK
  // ==========================================
  const handleUpload = async () => {
    // Validation
    if (parsedRows.length === 0) {
      setUploadError("Pehle data parse karo!");
      return;
    }
    if (!assignForm.title) {
      setUploadError("Task title required hai!");
      return;
    }
    if (!assignForm.assignedTo) {
      setUploadError("Team member select karo!");
      return;
    }
    if (!assignForm.dueDate) {
      setUploadError("Due date select karo!");
      return;
    }

    setUploadLoading(true);
    setUploadError("");
    setUploadSuccess("");

    try {
      const taskData = {
        title: assignForm.title,
        taskType: assignForm.taskType,
        assignedTo: assignForm.assignedTo,
        assignedName: assignForm.assignedName,
        assignedRole: assignForm.assignedRole || "",
        priority: assignForm.priority,
        dueDate: assignForm.dueDate,
        description: assignForm.description,
        assignedItems: parsedRows,
        totalItems: parsedRows.length,
        completedItems: [],
        progress: 0,
        status: "pending",
        createdBy: "admin",
        uploadSource: "excel_bulk",
      };

      const result = await TasksService.createBulkTask(taskData);

      setUploadSuccess(
        `✅ Task "${assignForm.title}" created! ${parsedRows.length} leads → ${assignForm.assignedName}`
      );

      // Reset
      setPasteData("");
      setParsedRows([]);
      setShowPreview(false);
      setAssignForm({
        title: "", taskType: "calling", assignedTo: "", assignedName: "",
        priority: "medium", dueDate: "", description: "",
      });

    } catch (error) {
      setUploadError("Upload failed: " + error.message);
    } finally {
      setUploadLoading(false);
    }
  };

  // ==========================================
  // RENDER
  // ==========================================
  return (
    <div>
      <PageHeader title="Bulk Data Upload" breadcrumbs={["Admin", "Bulk Upload"]} />

      {/* ========== FORMAT GUIDE ========== */}
    
      {/* ========== PASTE AREA ========== */}
      <div className="data-table-wrapper" style={{ marginBottom: 24 }}>
        <div className="data-table-toolbar">
          <div className="toolbar-title">
            <h3>📥 Paste Excel Data</h3>
          </div>
        </div>
        <div style={{ padding: 20 }}>
          <textarea
            value={pasteData}
            onChange={(e) => setPasteData(e.target.value)}
            placeholder={
              "Yahan pe Excel se copy kiya data paste karo...\n\n" +
              "Format:\n" +
              "LEAD001\tABC Technologies\tIT Services\t9876543210\n" +
              "LEAD002\tXYZ Manufacturing\tManufacturing\t9812345678\n" +
              "LEAD003\tPQR Retail\tRetail & Shopping\t9798765432"
            }
            rows={8}
            style={{
              width: "100%",
              padding: 16,
              border: "2px dashed #e5e7eb",
              borderRadius: 8,
              fontSize: 13,
              fontFamily: "monospace",
              resize: "vertical",
              outline: "none",
              backgroundColor: "#f9fafb",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
            onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
          />

          <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
            <button className="btn btn-primary" onClick={handleParse} disabled={uploadLoading}>
              🔍 Parse Data
            </button>
            <button
              className="btn btn-outline"
              onClick={() => {
                setPasteData("");
                setParsedRows([]);
                setShowPreview(false);
                setUploadError("");
                setUploadSuccess("");
              }}
              disabled={uploadLoading}
            >
              🗑 Clear
            </button>
          </div>
        </div>
      </div>

      {/* ========== STATUS MESSAGES ========== */}
      {uploadError && (
        <div style={{ marginBottom: 16, padding: 12, backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8 }}>
          <p style={{ color: "#991b1b", fontSize: 14, fontWeight: 500, margin: 0 }}>❌ {uploadError}</p>
        </div>
      )}
      {uploadSuccess && (
        <div style={{ marginBottom: 16, padding: 12, backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8 }}>
          <p style={{ color: "#166534", fontSize: 14, fontWeight: 500, margin: 0 }}>{uploadSuccess}</p>
        </div>
      )}

      {/* ========== PREVIEW TABLE ========== */}
      {showPreview && parsedRows.length > 0 && (
        <div className="data-table-wrapper" style={{ marginBottom: 24 }}>
          <div className="data-table-toolbar">
            <div className="toolbar-title">
              <h3>👁 Preview ({parsedRows.length} rows found)</h3>
            </div>
          </div>
          <div className="data-table-scroll" style={{ maxHeight: 400, overflowY: "auto" }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Lead ID</th>
                  <th>Business Name</th>
                  <th>Category</th>
                  <th>Contact Number</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {parsedRows.map((row, i) => (
                  <tr key={row.id || i}>
                    <td>{i + 1}</td>
                    <td className="font-bold text-blue-600">{row.leadId}</td>
                    <td className="font-semibold">{row.businessName || "—"}</td>
                    <td>
                      <span className="status-badge badge-source">{row.category || "—"}</span>
                    </td>
                    <td className="font-semibold">{row.contactNumber || "—"}</td>
                    <td>
                      <span className="status-badge badge-pending">pending</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ========== ASSIGN FORM ========== */}
      {showPreview && parsedRows.length > 0 && (
        <div className="data-table-wrapper">
          <div className="data-table-toolbar">
            <div className="toolbar-title">
              <h3>👤 Assign Task to Team Member</h3>
            </div>
          </div>
          <div style={{ padding: 20 }}>
            <div className="admin-form-group" style={{ marginBottom: 16 }}>
              <label>Task Title *</label>
              <input
                type="text"
                name="title"
                value={assignForm.title}
                onChange={handleAssignChange}
                placeholder="e.g. Calling Batch - IT Companies (Jan 2026)"
                disabled={uploadLoading}
              />
            </div>

            <div className="form-row">
              <div className="admin-form-group">
                <label>Task Type *</label>
                <select name="taskType" value={assignForm.taskType} onChange={handleAssignChange} disabled={uploadLoading}>
                  <option value="calling">📞 Calling / Data Collection</option>
                  <option value="data_entry">📝 Data Entry</option>
                  <option value="proposal">📄 Proposal</option>
                  <option value="demo">🖥 Demo</option>
                  <option value="follow_up">🔄 Follow-up</option>
                  <option value="other">📌 Other</option>
                </select>
              </div>
              <div className="admin-form-group">
                <label>Assign To *</label>
                <select name="assignedTo" value={assignForm.assignedTo} onChange={handleAssignChange} disabled={uploadLoading}>
                  <option value="">Select Team Member</option>
                  {teamMembers.filter((m) => m.isActive !== false).map((m) => (
                    <option key={m.id} value={m.id}>{m.name} ({m.role})</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="admin-form-group">
                <label>Priority</label>
                <select name="priority" value={assignForm.priority} onChange={handleAssignChange} disabled={uploadLoading}>
                  <option value="low">🟢 Low</option>
                  <option value="medium">🟡 Medium</option>
                  <option value="high">🔴 High</option>
                </select>
              </div>
              <div className="admin-form-group">
                <label>Due Date *</label>
                <input type="date" name="dueDate" value={assignForm.dueDate} onChange={handleAssignChange} disabled={uploadLoading} />
              </div>
            </div>

            <div className="admin-form-group" style={{ marginBottom: 16 }}>
              <label>Description / Instructions</label>
              <textarea
                name="description"
                value={assignForm.description}
                onChange={handleAssignChange}
                placeholder="Instructions for team member..."
                rows="2"
                disabled={uploadLoading}
              />
            </div>

            {/* Summary Box */}
            {assignForm.assignedName && (
              <div style={{ padding: 16, backgroundColor: "#f0f9ff", border: "1px solid #bfdbfe", borderRadius: 8, marginBottom: 16 }}>
                <h4 style={{ margin: "0 0 8px", fontSize: 14, color: "#1e40af" }}>📋 Upload Summary:</h4>
                <p style={{ margin: 0, fontSize: 14, color: "#374151", lineHeight: 1.8 }}>
                  <strong>{parsedRows.length}</strong> leads ko{" "}
                  <strong>"{assignForm.assignedName}"</strong> ({assignForm.assignedRole}) ko assign kiya ja raha hai.
                  <br />
                  Employee ko ye fields fill karni hongi:
                  <br />
                  📞 <strong>Call Date</strong> • 👤 <strong>Decision Maker</strong> • 📝 <strong>Requirement / Pain Point</strong>
                </p>
              </div>
            )}

            <div style={{ display: "flex", gap: 12 }}>
              <button className="btn btn-primary" onClick={handleUpload} disabled={uploadLoading} style={{ fontSize: 15, padding: "12px 24px" }}>
                {uploadLoading ? "⏳ Uploading..." : `🚀 Upload & Assign (${parsedRows.length} leads)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkUploadPage;